export type CarrierOfferStatus = "pending" | "accepted" | "declined" | "countered" | "expired";
export type CarrierOfferResponseType = "accept" | "decline" | "counter";

export interface CarrierOfferLoadSnapshot {
  loadId: string;
  reference: string;
  origin: { city: string; state: string };
  destination: { city: string; state: string };
  pickupWindowStart: string;
  deliveryWindowStart: string;
  equipmentType: string;
  carrierPayCents: number;
}

export interface CarrierOffer {
  tenantId: string;
  token: string;
  carrierName: string;
  carrierMc?: string;
  carrierEmail?: string;
  status: CarrierOfferStatus;
  expiresAt: string;
  load: CarrierOfferLoadSnapshot;
}

export function buildSignedCarrierOfferToken(args: {
  tenantId: string;
  loadId: string;
  carrierMc?: string;
  seed: string;
}) {
  return [
    "offer",
    normalizeTokenPart(args.tenantId),
    normalizeTokenPart(args.loadId),
    normalizeTokenPart(args.carrierMc ?? "carrier"),
    normalizeTokenPart(args.seed),
  ].join("_");
}

export function isCarrierOfferExpired(offer: CarrierOffer, nowIso: string) {
  return Date.parse(offer.expiresAt) <= Date.parse(nowIso);
}

export function buildCarrierOfferResponseDraft(args: {
  offer: CarrierOffer;
  responseType: CarrierOfferResponseType;
  signerName: string;
  nowIso: string;
  note?: string;
  counterAmountCents?: number;
}) {
  const expired = isCarrierOfferExpired(args.offer, args.nowIso);
  const blockedReasons: string[] = [];
  if (expired) blockedReasons.push("offer link expired");
  if (!args.signerName.trim()) blockedReasons.push("carrier signer required");
  if (args.responseType === "counter" && !args.counterAmountCents) {
    blockedReasons.push("counter amount required");
  }

  return {
    draftOnly: true,
    action: args.responseType === "accept" ? "capture_carrier_acceptance" : "record_carrier_response",
    responseType: args.responseType,
    ready: blockedReasons.length === 0,
    blockedReasons,
    loadId: args.offer.load.loadId,
    loadReference: args.offer.load.reference,
    carrierName: args.offer.carrierName,
    carrierMc: args.offer.carrierMc,
    signerName: args.signerName,
    counterAmountCents: args.counterAmountCents,
    note: args.note,
  };
}

export function formatCarrierPay(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function normalizeTokenPart(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 32);
}
